import React, { useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Divider from "@mui/material/Divider";
import { FormikProps } from "formik";
import { ExpenseFormData } from "./types";
import GenericExpenseFields from "./GenericExpenseFields";
import FuelExpenseFields from "./FuelExpenseFields";
import MaintenanceExpenseFields from "./MaintenanceExpenseFields";
import DriverCleanerExpenseFields from "./DriverCleanerExpenseFields";
import ParkingExpenseFields from "./ParkingExpenseFields";
import PaymentDetailsFields from "./PaymentDetailsFields";
import ChallanExpense from "./ChallanExpense";
import DeletedPayments from "../../rentalBooking/components/DeletedPayments";
import {
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import DeletedExpenseDetails from "../../rentalBooking/components/DeletedExpenseDetails";
import Button from "@mui/material/Button";
import { DocumentUploadSection } from "../../../components/DocumentUploadSection";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import { AccordionDetails } from "@mui/material";
import OutsourcedOwnerExpenseFields from "./OutsourcedOwnerExpense";

interface ExpenseFormProps {
  type: string;
  index: number;
  typeIndex: number;
  onDelete: () => void;
  formikProps: FormikProps<ExpenseFormData>;
  showDelete?: boolean; // Optional prop to control delete button visibility
}

function ExpenseForm({
  type,
  index,
  typeIndex,
  onDelete,
  formikProps,
  showDelete = true,
}: ExpenseFormProps) {
  const { values, setFieldValue } = formikProps;

  const [showChangeLogs, setShowChangeLogs] = useState(false);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 1.5,
        borderRadius: 1,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
          }}
        >
          Entry #{typeIndex + 1}
        </Typography>
        {showDelete && !values?.expenses?.[index]?.isDeleted && (
          <IconButton
            onClick={onDelete}
            color="error"
            size="small"
            sx={{
              p: 0.5,
              "& .MuiSvgIcon-root": {
                fontSize: "1rem",
              },
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
        {values?.expenses?.[index]?.isDeleted && (
          <Button
            size="small"
            sx={{
              ml: 1,
              p: 0.5,
              fontSize: "0.75rem",
              fontStyle: "italic",
            }}
            onClick={() => {
              formikProps.setFieldValue(`expenses.${index}.isDeleted`, false);
              const keyToAdd = values?.expenses?.[index]?.id;
              if (!formikProps.values.odometerReadings?.has(keyToAdd!)) {
                const newMap = new Map(formikProps.values.odometerReadings);
                newMap.set(keyToAdd!, {
                  index: formikProps.values.odometerReadings?.size || 0,
                  odometerReading: parseInt(
                    values?.expenses?.[index]?.odometerReading || "0"
                  ),
                  fuelQuantity: parseInt(
                    values?.expenses?.[index]?.liters || "0"
                  ),
                  readingDate: values?.expenses?.[index]?.expenseDate || "",
                });
                formikProps.setFieldValue("odometerReadings", newMap);
              }
            }}
          >
            Undo Delete
          </Button>
        )}
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
        {type === "Fuel" && <FuelExpenseFields index={index} />}
        {type === "Maintenance / Repair" && (
          <MaintenanceExpenseFields index={index} />
        )}
        {type === "Driver/Cleaner" && (
          <DriverCleanerExpenseFields index={index} />
        )}
        {type === "Parking" && <ParkingExpenseFields index={index} />}
        {type === "Challan" && <ChallanExpense index={index} />}
        {type === "Outsourced Owner" && (
          <OutsourcedOwnerExpenseFields index={index} />
        )}
        {["Other", "Insurance"].includes(type) && (
          <GenericExpenseFields index={index} />
        )}
        <PaymentDetailsFields index={index} />
        {values?.expenses?.[index]?.deletedPayments &&
          values?.expenses?.[index]?.deletedPayments?.length! > 0 && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "gray",
                  fontSize: "0.75rem",
                }}
                onClick={() => {
                  setShowChangeLogs(!showChangeLogs);
                }}
              >
                Payment Change Logs
                {showChangeLogs ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </Typography>
              {showChangeLogs && (
                <>
                  <DeletedPayments
                    deletedPaymentDetails={
                      values?.expenses?.[index]?.deletedPayments
                    }
                  />
                </>
              )}
            </Grid>
          )}

        <Grid item xs={9}>
          <Accordion
            sx={{
              border: "none",
              elevation: 0,
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="expense-proofs-content"
              id="expense-proofs-header"
            >
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                Expense Proofs
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DocumentUploadSection
                title=""
                selectedFiles={values?.expenses?.[index]?.selectedFiles || []}
                uploadedFiles={values?.expenses?.[index]?.uploadedFiles || []}
                onFileSelect={(files: File[]) => {
                  setFieldValue(`expenses.${index}.selectedFiles`, files);
                }}
                onFileRemove={(fileIndex: number) => {
                  const currentFiles =
                    values?.expenses?.[index]?.selectedFiles || [];
                  const updatedFiles = currentFiles.filter(
                    (_, i) => i !== fileIndex
                  );
                  setFieldValue(
                    `expenses.${index}.selectedFiles`,
                    updatedFiles
                  );
                }}
                onPreview={(url) => {
                  window.open(url, "_blank");
                }}
                isLoading={false}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ExpenseForm;
